import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  useScroll,
} from "framer-motion";
import { Mail, ArrowDown, Play } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1679391029864-d46f366a456b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxzb2NjZXIlMjBzdGFkaXVtJTIwZmxvb2RsaWdodHMlMjBuaWdodCUyMGF0bW9zcGhlcmV8ZW58MHx8fHwxNzg4NzE5NTc4fDA&ixlib=rb-4.1.0&q=85&w=1920&auto=format&fit=crop";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero({ t, count, onJoin, onContact }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useTransform(my, [0, 1], [9, -9]);
  const rotateY = useTransform(mx, [0, 1], [-11, 11]);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const lines = [t.hero.line1, t.hero.line2];

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={(e) => {
        mx.set(e.clientX / window.innerWidth);
        my.set(e.clientY / window.innerHeight);
      }}
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt=""
          className="h-[120%] w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#06080C]/70 via-[#06080C]/85 to-[#06080C]" />
        <div className="absolute left-1/2 top-0 h-[60vh] w-[80vw] -translate-x-1/2 rounded-full bg-[#00F562]/10 blur-[140px]" />
      </motion.div>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-[1.2fr_0.8fr] lg:px-10">
        <div>
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-[#00F562]"
          >
            {t.hero.overline}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.15 + i * 0.14, ease: EASE }}
                  className={`block ${i === 1 ? "italic text-[#00F562]" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-join-waitlist-button"
              onClick={onJoin}
              className="group flex items-center gap-3 rounded-full bg-[#00F562] px-7 py-4 text-sm font-extrabold uppercase tracking-widest text-[#06080C] transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,245,98,0.55)]"
            >
              {t.hero.ctaPrimary}
              <ArrowDown
                size={16}
                className="transition-transform duration-300 group-hover:translate-y-1"
              />
            </button>
            <button
              data-testid="hero-contact-button"
              type="button"
              onClick={onContact}
              className="flex items-center gap-3 rounded-full border border-white/15 px-7 py-4 text-sm font-bold uppercase tracking-widest text-slate-200 transition-all duration-300 hover:border-[#00F562]/60 hover:text-[#00F562]"
            >
              <Mail size={16} />
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-counter-badge"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 inline-flex items-center gap-4 rounded-2xl border border-[#00F562]/25 bg-[#00F562]/[0.04] px-5 py-3.5 shadow-[0_0_40px_rgba(0,245,98,0.08)] backdrop-blur"
          >
            <span className="relative flex h-3 w-3 flex-shrink-0" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00F562] opacity-70" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-[#00F562]" />
            </span>
            <div className="flex items-baseline gap-2">
              <motion.span
                key={count === null ? "na" : `hc-${count}`}
                initial={{ opacity: 0, y: 8, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="font-display text-3xl font-black leading-none text-[#00F562] tabular-nums"
                data-testid="hero-counter-number"
              >
                {count === null ? "—" : count}
              </motion.span>
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">
                {t.hero.counterSuffix}
              </span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="hidden justify-center lg:flex"
          style={{ perspective: 1200 }}
        >
          <motion.div
            data-testid="hero-holo-card"
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative w-80 rounded-2xl border border-[#00F562]/25 bg-gradient-to-b from-[#0D111A]/90 to-[#06080C]/95 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.6),0_0_60px_rgba(0,245,98,0.08)] backdrop-blur-md"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00F562]">
                {t.hero.cardTag}
              </span>
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#00F562]" />
            </div>
            <div
              className="mt-5 flex h-44 items-center justify-center rounded-xl border border-white/10 bg-[#141A26]"
              style={{ transform: "translateZ(30px)" }}
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#00F562]/15 text-[#00F562]">
                <Play size={22} fill="currentColor" />
              </span>
            </div>
            <p
              className="mt-5 font-display text-lg font-extrabold uppercase tracking-wide"
              style={{ transform: "translateZ(20px)" }}
            >
              {t.hero.cardName}
            </p>
            <p className="text-sm italic text-slate-400">{t.hero.cardYou}</p>
            <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">
              {t.hero.cardChips.map((s) => (
                <span
                  key={s}
                  className="rounded-md border border-white/10 px-2 py-1.5 text-center"
                >
                  {s}
                </span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
